/**
 * Push Notification Hook
 * Provides push notification functionality to React components
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getPushNotificationService, type PushNotificationService } from '../../application/services/PushNotificationService';
import type { PushNotificationState, PushNotificationPayload } from '../../domain/models/PushNotification';

interface UsePushNotificationOptions {
  /**
   * Custom user ID for subscription
   */
  customerId?: string;
  /**
   * Visitor ID for subscription
   */
  visitorId?: string;
  /**
   * Auto-initialize on mount
   * @default true
   */
  autoInitialize?: boolean;
  /**
   * Callback when a foreground message is received
   */
  onMessage?: (payload: PushNotificationPayload) => void;
  /**
   * Auto-show browser notification for foreground messages
   * @default true
   */
  autoShowNotification?: boolean;
}

interface UsePushNotificationReturn extends PushNotificationState {
  /**
   * Initialize push notification service
   */
  initialize: () => Promise<void>;
  /**
   * Request permission and subscribe
   */
  requestPermission: () => Promise<string | null>;
  /**
   * Check if should show permission prompt
   */
  shouldShowPrompt: () => boolean;
  /**
   * Dismiss the permission prompt (don't ask again for a while)
   */
  dismissPrompt: () => void;
}

/**
 * Hook for managing push notifications
 */
export function usePushNotification(
  options: UsePushNotificationOptions = {}
): UsePushNotificationReturn {
  const {
    customerId,
    visitorId,
    autoInitialize = true,
    onMessage,
    autoShowNotification = true,
  } = options;

  const serviceRef = useRef<PushNotificationService | null>(null);

  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    permission: 'default',
    deviceToken: null,
    isLoading: false,
    error: null,
  });

  /**
   * Initialize the push notification service
   */
  const initialize = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const service = getPushNotificationService();
      serviceRef.current = service;

      await service.initialize();

      setState((prev) => ({
        ...prev,
        isSupported: service.isSupported(),
        permission: service.getPermissionStatus(),
        isLoading: false,
      }));

      // Set up message listener
      service.onMessage((payload) => {
        if (onMessage) {
          onMessage(payload);
        }

        if (autoShowNotification) {
          service.showNotification(payload);
        }
      });

    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to initialize push notifications',
      }));
    }
  }, [onMessage, autoShowNotification]);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async (): Promise<string | null> => {
    if (!serviceRef.current) {
      console.warn('Push notification service not initialized');
      return null;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const token = await serviceRef.current.requestPermissionAndSubscribe(
        customerId,
        visitorId
      );

      setState((prev) => ({
        ...prev,
        permission: serviceRef.current?.getPermissionStatus() || 'default',
        deviceToken: token,
        isLoading: false,
      }));

      return token;

    } catch (error) {
      console.error('Failed to request permission:', error);
      setState((prev) => ({
        ...prev,
        permission: serviceRef.current?.getPermissionStatus() || 'default',
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to request permission',
      }));
      return null;
    }
  }, [customerId, visitorId]);

  /**
   * Check if should show permission prompt
   */
  const shouldShowPrompt = useCallback((): boolean => {
    return serviceRef.current?.shouldShowPrompt() ?? false;
  }, []);

  /**
   * Dismiss the permission prompt
   */
  const dismissPrompt = useCallback((): void => {
    serviceRef.current?.dismissPrompt();
  }, []);

  // Auto-initialize on mount
  useEffect(() => {
    if (autoInitialize) {
      initialize();
    }

    return () => {
      serviceRef.current?.destroy();
    };
  }, [autoInitialize, initialize]);

  return {
    ...state,
    initialize,
    requestPermission,
    shouldShowPrompt,
    dismissPrompt,
  };
}
